import { Droppable, Draggable } from '@hello-pangea/dnd';
import { BacklogCard } from './BacklogCard';
import { ItBacklogItem, BacklogStatus, STATUS_CONFIG } from '@/types/itBacklog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface BacklogColumnProps {
  status: BacklogStatus;
  items: ItBacklogItem[];
  onItemClick: (item: ItBacklogItem) => void;
}

export function BacklogColumn({ status, items, onItemClick }: BacklogColumnProps) {
  const config = STATUS_CONFIG[status];
  const totalHours = items.reduce((sum, item) => sum + (item.estimated_hours || 0), 0);
  
  return (
    <div className="flex flex-col h-full min-w-[280px] max-w-[320px]">
      {/* Column Header */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-t-lg border-b">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">{config.label}</h3>
          <Badge variant="secondary" className="text-xs">
            {items.length}
          </Badge>
        </div>
        {totalHours > 0 && (
          <span className="text-xs text-muted-foreground">
            {totalHours}h
          </span>
        )}
      </div>
      
      {/* Droppable Area */}
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <ScrollArea 
            className={`flex-1 p-2 rounded-b-lg transition-colors ${
              snapshot.isDraggingOver 
                ? 'bg-primary/5 ring-2 ring-primary/20' 
                : 'bg-muted/30'
            }`}
          >
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2 min-h-[200px]"
            >
              {items.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <BacklogCard
                        item={item}
                        onClick={() => onItemClick(item)}
                        isDragging={snapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              
              {items.length === 0 && (
                <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                  Nenhuma tarefa
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </Droppable>
    </div>
  );
}
